import dynamic from "next/dynamic";
import { toast } from "sonner";
import { useCompanyDatabaseStore } from "~/app/sales/accounts/store";
import { usePeopleDatabaseStore } from "~/app/sales/contacts/store";

import { routes } from "~/core/constants/routes";
import { useSignout } from "~/core/hooks/use-signout";
import ClipboardListOutlinedIcon from "~/core/ui/CustomIcons/ClipboardList";

import { useGlobalComposeEmailModalStore } from "~/components/GlobalComposeEmailModal/store";
import { useGlobalCreateOpportunityStore } from "~/components/GlobalNewOpportunityModal/store";
import { useGlobalCreateTaskStore } from "~/components/GlobalNewTaskModal/store";
import { useGlobalNoteModalStore } from "~/components/GlobalNoteModal/store";

import useCurrentOrganization from "~/lib/organizations/hooks/use-current-organization";

import { faRightFromBracket } from "@fortawesome/pro-regular-svg-icons";

import ItemIconLoading from "../components/ItemIcon/loading";
import { useGlobalCommandbarStore } from "../store";
import type { ICommandbarItem } from "../types/common";
import { CommandbarFilterTabType, CommandbarItemType } from "../types/common";

const CreateAccountIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateAccountIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateAutomationIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateAutomationIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateNoteIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateNoteIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateTaskIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateTaskIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateContactIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateContactIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateFormIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateFormIcon"),
  { loading: () => <ItemIconLoading /> },
);
const CreateOpportunityIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/CreateOpportunityIcon"),
  { loading: () => <ItemIconLoading /> },
);
const SwitchWorkspaceIcon = dynamic(
  () => import("~/core/ui/CustomIcons/SwitchWorkspace"),
  { loading: () => <ItemIconLoading /> },
);
const ComposeEmailIcon = dynamic(
  () => import("~/core/ui/CustomIcons/Create/ComposeEmailIcon"),
  { loading: () => <ItemIconLoading /> },
);

export const useStaticCommandItems = () => {
  const { signout } = useSignout();
  const currentOrg = useCurrentOrganization();

  const setIsOpenGlobalCreateOpportunity = useGlobalCreateOpportunityStore(
    state => state.setIsOpenGlobalCreateOpportunity,
  );
  const setIsOpenAddCompanyModal = useCompanyDatabaseStore(
    state => state.setIsOpenAddCompanyModal,
  );
  const setIsOpenCreateContactModal = usePeopleDatabaseStore(
    state => state.setIsOpenCreateContactModal,
  );
  const setOpenGlobalComposeEmailModal = useGlobalComposeEmailModalStore(
    state => state.setOpenGlobalComposeEmailModal,
  );
  const setOpenGlobalCreateNoteModal = useGlobalNoteModalStore(
    state => state.setOpenGlobalCreateNoteModal,
  );
  const setIsOpenGlobalCreateTask = useGlobalCreateTaskStore(
    state => state.setIsOpenGlobalCreateTask,
  );
  const setOpenSwitchWorkspaceModal = useGlobalCommandbarStore(
    st => st.setOpenSwitchWorkspaceModal,
  );

  const copyOrgId = () => {
    navigator.clipboard.writeText(currentOrg?.org_id ?? "");
    toast.success("Workspace ID copied to clipboard");
  };

  const commandbarStaticCommands: ICommandbarItem[] = [
    {
      id: "create-opportunity",
      label: "Create opportunity",
      customIcon: <CreateOpportunityIcon />,
      type: CommandbarItemType.COMMAND,
      objectType: CommandbarFilterTabType.OPPORTUNITY,
      onClick: () => setIsOpenGlobalCreateOpportunity(true),
      ctaLabel: "Create opportunity",
    },
    {
      id: "create-account",
      label: "Create account",
      customIcon: <CreateAccountIcon />,
      type: CommandbarItemType.COMMAND,
      objectType: CommandbarFilterTabType.ACCOUNT,
      onClick: () => setIsOpenAddCompanyModal(true),
      ctaLabel: "Create account",
    },
    {
      id: "compose-email",
      label: "Compose email",
      customIcon: <ComposeEmailIcon />,
      type: CommandbarItemType.COMMAND,
      onClick: () => setOpenGlobalComposeEmailModal(true),
      ctaLabel: "Compose",
    },
    {
      id: "create-contact",
      label: "Create contact",
      customIcon: <CreateContactIcon />,
      type: CommandbarItemType.COMMAND,
      objectType: CommandbarFilterTabType.CONTACT,
      onClick: () => setIsOpenCreateContactModal(true),
      ctaLabel: "Create contact",
    },
    {
      id: "create-form",
      label: "Create form",
      customIcon: <CreateFormIcon />,
      type: CommandbarItemType.COMMAND,
      href: routes.forms.index,
      ctaLabel: "Create form",
    },
    {
      id: "create-automation",
      label: "Create automation",
      customIcon: <CreateAutomationIcon />,
      type: CommandbarItemType.COMMAND,
      href: routes.workflows.index,
      ctaLabel: "Create automation",
    },
    {
      id: "create-note",
      label: "Create note",
      customIcon: <CreateNoteIcon />,
      type: CommandbarItemType.COMMAND,
      onClick: () => setOpenGlobalCreateNoteModal(true),
      ctaLabel: "Create note",
    },
    {
      id: "create-task",
      label: "Create task",
      customIcon: <CreateTaskIcon />,
      type: CommandbarItemType.COMMAND,
      onClick: () => setIsOpenGlobalCreateTask(true),
      ctaLabel: "Create task",
    },
    {
      id: "switch-workspace",
      label: "Switch workspace",
      customIcon: <SwitchWorkspaceIcon />,
      type: CommandbarItemType.COMMAND,
      onClick: () => setOpenSwitchWorkspaceModal(true),
      ctaLabel: "Switch",
    },
    {
      id: "copy-org-id",
      label: "Copy workspace id",
      description: `(${currentOrg?.org_id})`,
      customIcon: <ClipboardListOutlinedIcon />,
      type: CommandbarItemType.COMMAND,
      onClick: copyOrgId,
      ctaLabel: "Copy",
    },
    {
      id: "logout",
      label: "Logout",
      faIcon: faRightFromBracket,
      type: CommandbarItemType.COMMAND,
      onClick: signout,
      ctaLabel: "Logout",
    },
  ];

  return { commandbarStaticCommands };
};
